/** A place a cited source names (e.g. an OpenStreetMap hospital), in WGS84. */
export interface CitedLocation {
  name: string;
  lat: number;
  lon: number;
  category?: string;
}

const isCoordinate = (value: unknown, limit: number): value is number =>
  typeof value === 'number' &&
  Number.isFinite(value) &&
  Math.abs(value) <= limit;

/**
 * The valid `metadata.locations` of a cited source; anything malformed is
 * dropped.
 */
export function citedLocationsOf(metadata: unknown): CitedLocation[] {
  const raw = (metadata as { locations?: unknown } | null | undefined)
    ?.locations;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    const { name, lat, lon, category } = (entry ?? {}) as Record<
      string,
      unknown
    >;
    if (!isCoordinate(lat, 90) || !isCoordinate(lon, 180)) return [];
    return [
      {
        name:
          typeof name === 'string' && name.trim() ? name.trim() : '(unnamed)',
        lat,
        lon,
        ...(typeof category === 'string' && category ? { category } : {}),
      },
    ];
  });
}

/** Zoom used when centering on a single facility. */
export const LOCATION_ZOOM = 15;

/**
 * A center and web-mercator zoom that shows every location. The zoom is picked
 * from the wider of the two spans, with a step of padding so the outermost
 * points don't sit on the map edge.
 */
export function locationsView(
  locations: CitedLocation[]
): { latitude: number; longitude: number; zoom: number } | null {
  if (locations.length === 0) return null;
  const lats = locations.map((location) => location.lat);
  const lons = locations.map((location) => location.lon);
  const south = Math.min(...lats);
  const north = Math.max(...lats);
  const west = Math.min(...lons);
  const east = Math.max(...lons);
  const span = Math.max(north - south, (east - west) / 2);
  const zoom =
    span <= 0 ? LOCATION_ZOOM : Math.floor(Math.log2(180 / span)) - 1;
  return {
    latitude: (south + north) / 2,
    longitude: (west + east) / 2,
    zoom: Math.min(LOCATION_ZOOM, Math.max(3, zoom)),
  };
}
